import { createSelector } from "@reduxjs/toolkit";

import {
  hasData,
  hasFailedData,
  isLoading,
} from "@/store/apiRequestStatusMachine";
import type { RootState } from "@/store/slices";
import { EMPTY_ARRAY } from "@/store/utils";

import { selectProductLayout } from "./slice";
import type { ProductItem, ProductListItem } from "./types";
import { ProductListItemType } from "./types";

export const selectIsProductLayoutLoading = (state: RootState) => {
  const productLayout = selectProductLayout(state);
  return isLoading(productLayout);
};

export const selectIsProductLayoutFailed = (state: RootState) => {
  const productLayout = selectProductLayout(state);
  return hasFailedData(productLayout) && !isLoading(productLayout);
};

export const selectSections = (state: RootState) => {
  const productLayout = selectProductLayout(state);
  return hasData(productLayout) ? productLayout.state.sections : null;
};

export const selectProducts = (state: RootState) => {
  const productLayout = selectProductLayout(state);
  return hasData(productLayout) ? productLayout.state.products : null;
};

export const selectProductById = (state: RootState, id: number) => {
  const products = selectProducts(state);
  return products?.find((p) => p.id === id);
};

export const selectProductsByIds = (state: RootState, ids: number[]) => {
  const products = selectProducts(state);
  return products?.filter((p) => ids.includes(p.id));
};

export const selectCrossSellProducts = createSelector(
  [selectProducts],
  (products): ProductItem[] => {
    if (!products) {
      return EMPTY_ARRAY;
    }

    const crossSellProducts = products.filter((p) => p.isCrossSells);
    return crossSellProducts.length > 0 ? crossSellProducts : EMPTY_ARRAY;
  },
);

export const selectAllProductsInSections = createSelector(
  [selectSections, selectProducts],
  (sections, products): ProductItem[] | undefined => {
    if (!sections || !products) {
      return undefined;
    }

    const productIdsSet = new Set<number>();

    const addItem = (item: ProductListItem) => {
      if (
        item.type === ProductListItemType.ITEM ||
        item.type === ProductListItemType.VARIABLE
      ) {
        productIdsSet.add(item.id);
      } else if (item.items.length > 0) {
        for (const element of item.items) {
          addItem(element);
        }
      }
    };

    for (const section of sections) {
      for (const element of section.items) {
        addItem(element);
      }
    }

    const productIds = [...productIdsSet];

    if (productIds.length > 0) {
      return products.filter((p) => productIds.includes(p.id));
    }

    return undefined;
  },
);
